import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, CircularProgress, IconButton, Snackbar, TextField } from '@mui/material';
import { Add, DeleteOutline, Insights, MonitorWeightOutlined, ShowChart } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './MetricTrackingPage.css';

const decimal = (value) => value?.$numberDecimal ?? value ?? '';

export default function MetricTrackingPage({ type, title, description, field, unit, inputLabel }) {
  const { id: routeUserId } = useParams();
  const { authToken } = useAuth();
  const [entries, setEntries] = useState([]);
  const [value, setValue] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });

  const load = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/tracking/${type}`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!response.ok) throw new Error('No se pudo cargar el historial');
      const data = await response.json();
      setEntries(Array.isArray(data) ? data.sort((a, b) => new Date(b.date) - new Date(a.date)) : []);
    } catch (error) {
      setNotice({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [type, authToken]);

  const current = useMemo(() => entries[0] ? decimal(entries[0][field]) : null, [entries, field]);

  const addEntry = async () => {
    if (!value || Number(value) <= 0) {
      setNotice({ open: true, message: 'Introduce un valor válido', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/tracking/${type}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: routeUserId, [field]: Number(value), date: new Date(`${date}T12:00:00`).toISOString() }),
      });
      if (!response.ok) throw new Error('No se pudo guardar la medición');
      setValue('');
      await load();
      setNotice({ open: true, message: 'Medición guardada', severity: 'success' });
    } catch (error) {
      setNotice({ open: true, message: error.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (entryId) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/tracking/${type}/${entryId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      if (!response.ok) throw new Error('No se pudo eliminar la medición');
      setEntries((currentEntries) => currentEntries.filter((entry) => entry._id !== entryId));
    } catch (error) {
      setNotice({ open: true, message: error.message, severity: 'error' });
    }
  };

  return <main className="metric-page">
    <header className="metric-page-hero">
      <div><span>SEGUIMIENTO CORPORAL</span><h1>{title}</h1><p>{description}</p></div>
      <div className="metric-page-current"><MonitorWeightOutlined /><span><small>ÚLTIMO REGISTRO</small><strong>{current ?? '—'} <b>{current != null ? unit : ''}</b></strong></span></div>
    </header>
    <div className="metric-page-layout">
      <section className="metric-page-form">
        <div className="metric-page-section-title"><Add /><span><strong>Nueva medición</strong><small>Registra el dato y la fecha correspondiente.</small></span></div>
        <TextField fullWidth type="number" label={inputLabel} value={value} onChange={(event) => setValue(event.target.value)} inputProps={{ step: '0.1' }} />
        <TextField fullWidth type="date" label="Fecha" value={date} onChange={(event) => setDate(event.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="contained" fullWidth disabled={saving} onClick={addEntry}>{saving ? <CircularProgress size={19} color="inherit" /> : <Add />} Guardar medición</Button>
      </section>
      <section className="metric-page-history">
        <div className="metric-page-section-title"><ShowChart /><span><strong>Historial</strong><small>{entries.length} mediciones registradas</small></span></div>
        {loading ? <div className="metric-page-empty"><CircularProgress size={26} /></div> : entries.length ? <div className="metric-page-list">{entries.map((entry, index) => <article key={entry._id}><i><Insights /></i><div><strong>{decimal(entry[field])} {unit}</strong><small>{new Date(entry.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</small></div>{index === 0 && <span>Última</span>}<IconButton aria-label="Eliminar medición" onClick={() => removeEntry(entry._id)}><DeleteOutline /></IconButton></article>)}</div> : <div className="metric-page-empty"><Insights /><strong>Aún no hay mediciones</strong><p>Añade el primer registro para empezar a ver tu evolución.</p></div>}
      </section>
    </div>
    <Snackbar open={notice.open} autoHideDuration={3500} onClose={() => setNotice((currentNotice) => ({ ...currentNotice, open: false }))}><Alert severity={notice.severity}>{notice.message}</Alert></Snackbar>
  </main>;
}
